import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { CrmContactFilter } from '../contacts/contactList';
import { ListOption, TagOption } from '../contacts/filters/types';
import { TargetChoice, TargetPicker } from '../tagging/TargetPicker';
import { people, selectionChangedTo } from '../tagging/tagging';
import { CRM_LIST_CHOICES, CRM_TAG_CHOICES } from '../tagging/tagging.queries';
import { useDebounced } from '../tagging/useDebounced';
import {
  Admin,
  adminName,
  BATCH_LIMITS,
  BATCH_TABS,
  BatchForm,
  BatchPreview,
  BatchRecord,
  BatchTab,
  changeInput,
  confirmBatchLabel,
  describeBatchError,
  describePreview,
  describeUndo,
  emptyBatchForm,
  isWorking,
  progressOf,
  PROGRESS_POLL_MS,
  FieldsForm,
  formProblems,
  isRisky,
  MEMBERSHIP_STATUSES,
  METHODS,
  MISSING_TABS,
  opensConsent,
  otherGroupEdits,
  OUTCOMES,
  SUPPORT_LEVELS,
  TAB_GROUP,
  tabHasEdits,
  YesNo,
} from './batchUpdate';
import {
  CRM_BATCH_CHOICES,
  CRM_BATCH_PREVIEW,
  CRM_BATCH_UPDATE,
  CRM_RUN_BATCH_UPDATE,
  CRM_UNDO_BATCH_UPDATE,
} from './batchUpdate.queries';

interface BatchUpdateDialogProps {
  /** Who it applies to, as the API takes it: ticked ids, the list's filter, or a list's people. */
  filter: CrmContactFilter;
  /** Who, in words, without a full stop: "The contacts you ticked", "Everyone on Callers". */
  who: string;
  /** No search and no filter: "everyone" is the whole CRM. */
  everyone?: boolean;
  /** The list it is run from, recorded with the batch. */
  segmentId?: number;
  initialTab?: BatchTab;
  /** `changed`: a batch ran (or was undone), so what the screen shows is stale. */
  onClose: (changed: boolean) => void;
}

interface Choices {
  crmAdmins: Array<Admin>;
  crmMembershipPlans: Array<{ id: number; name: string; total: number; active: number }>;
  stages: Array<{ value: string | null; count: number }>;
}

type Step = 'edit' | 'review' | 'done';

const NO_CHANGE = 'No change';

const n = (value: number) => value.toLocaleString('en-US');

/** A select with NationBuilder's "Select" meaning no change. */
const Choice = ({
  label,
  value,
  onChange,
  options,
  hint,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  hint?: string;
  disabled?: boolean;
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: { xs: 0.5, sm: 2 }, alignItems: 'center' }}>
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
    >
      <MenuItem value="">
        <em>{NO_CHANGE}</em>
      </MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * A NationBuilder tab this CRM has no data for: shown, off, with why on hover. Tabs hands its
 * children props of its own (fullWidth, selectionFollowsFocus, …); they go to the Tab, not the
 * span a disabled element needs for its tooltip.
 */
const MissingTab = ({ why, label, ...tabProps }: { why: string; label: string; value: string }) => (
  <Tooltip title={why}>
    <span>
      <Tab {...tabProps} label={label} disabled sx={{ textTransform: 'none', minWidth: 0 }} />
    </span>
  </Tooltip>
);

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const Overline = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="overline" color="text.secondary" component="p" sx={{ lineHeight: 1.6 }}>
    {children}
  </Typography>
);

/** The parts of a preview or a result: what, how many change, how many already so. */
const PartsTable = ({ parts, done }: { parts: BatchPreview['parts']; done: boolean }) => (
  <Table size="small" aria-label={done ? 'What changed' : 'What will change'}>
    <TableHead>
      <TableRow>
        <TableCell>Change</TableCell>
        <TableCell align="right">{done ? 'Changed' : 'Will change'}</TableCell>
        <TableCell align="right">Already so</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {parts.map((part) => (
        <TableRow key={part.key}>
          <TableCell sx={{ wordBreak: 'break-word' }}>{part.label}</TableCell>
          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {n(part.changed)}
          </TableCell>
          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
            {part.unchanged == null ? '—' : n(part.unchanged)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/**
 * NationBuilder's batch update (card 20), on a selection of the contact list or
 * everyone on a list. Three steps:
 *
 * 1. **The change,** on NationBuilder's tabs. People, Consent and Clear field
 *    set contact fields together; Tags, Lists, Log contacts and Memberships are
 *    each their own kind. A batch makes one kind of change.
 * 2. **Review:** the API counts the selection now and says, per part, how many
 *    it changes and how many already have the value, and what to know first.
 *    Its count is the one confirmed.
 * 3. **Done,** with what changed and an undo. On a changed count nothing was
 *    changed: the review is asked again and the new number confirmed.
 */
export const BatchUpdateDialog = ({ filter, who, everyone, segmentId, initialTab, onClose }: BatchUpdateDialogProps) => {
  const client = useApolloClient();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<BatchForm>(() => emptyBatchForm(initialTab));
  const [step, setStep] = useState<Step>('edit');
  const [preview, setPreview] = useState<BatchPreview>();
  const [result, setResult] = useState<BatchRecord>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [changedSomething, setChangedSomething] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);

  const group = TAB_GROUP[form.tab];
  const choices = useQuery<Choices>(CRM_BATCH_CHOICES);
  const tagsQuery = useQuery<{ crmTags: Array<TagOption> }>(CRM_TAG_CHOICES, { skip: group !== 'tags' });
  const [listText, setListText] = useState('');
  const listSearch = useDebounced(listText.trim());
  const listsQuery = useQuery<{ crmSegments: { total: number; items: Array<ListOption> } }>(CRM_LIST_CHOICES, {
    variables: listSearch ? { search: listSearch } : {},
    skip: group !== 'lists',
  });

  const tagChoices: Array<TargetChoice> = useMemo(
    () =>
      (tagsQuery.data?.crmTags ?? [])
        .filter((tag) => !tag.isSystem)
        .map((tag) => ({ id: tag.id, name: tag.name, color: tag.color, count: tag.contactCount })),
    [tagsQuery.data],
  );
  const listChoices: Array<TargetChoice> = useMemo(
    () => (listsQuery.data?.crmSegments.items ?? []).map((list) => ({ id: list.id, name: list.name, count: list.contactCount })),
    [listsQuery.data],
  );

  const problems = formProblems(form);
  const elsewhere = otherGroupEdits(form);

  const setFields = (patch: Partial<FieldsForm>) => setForm((f) => ({ ...f, fields: { ...f.fields, ...patch } }));
  const edit = (patch: Partial<BatchForm>) => setForm((f) => ({ ...f, ...patch }));

  /** Asks the API what the change would do. `reason`: an error to keep showing, as after a changed count. */
  const review = async (reason?: unknown) => {
    setBusy(true);
    setError(reason);
    try {
      const { data } = await client.query<{ crmBatchUpdatePreview: BatchPreview }>({
        query: CRM_BATCH_PREVIEW,
        variables: { filter, change: changeInput(form), ...(segmentId ? { segmentId } : {}) },
        fetchPolicy: 'no-cache',
      });
      setPreview(data.crmBatchUpdatePreview);
      setStep('review');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // A batch too large for one request goes on in the background: ask for its progress until it
  // is done, while this dialog is open. Closing stops asking; the batch carries on.
  const open = useRef(true);
  useEffect(
    () => () => {
      open.current = false;
    },
    []
  );
  const follow = async (record: BatchRecord) => {
    let current = record;
    setResult(current);
    while (open.current && isWorking(current)) {
      await new Promise((resolve) => setTimeout(resolve, PROGRESS_POLL_MS));
      if (!open.current) return;
      try {
        const { data } = await client.query<{ crmBatchUpdate: BatchRecord }>({
          query: CRM_BATCH_UPDATE,
          variables: { id: current.id },
          fetchPolicy: 'no-cache',
        });
        current = data.crmBatchUpdate;
        if (open.current) setResult(current);
      } catch {
        // A missed answer: ask again at the next tick.
      }
    }
  };

  const run = async () => {
    if (!preview) return;
    setBusy(true);
    setError(undefined);
    try {
      const { data } = await client.mutate<{ crmRunBatchUpdate: BatchRecord }>({
        mutation: CRM_RUN_BATCH_UPDATE,
        variables: { filter, expectedCount: preview.matched, change: changeInput(form), ...(segmentId ? { segmentId } : {}) },
      });
      setChangedSomething(true);
      setStep('done');
      setBusy(false);
      await follow(data!.crmRunBatchUpdate);
    } catch (err) {
      setError(err);
      // Nothing was changed: count again, and show the new numbers to confirm.
      if (selectionChangedTo(err) !== undefined) await review(err);
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!result) return;
    setBusy(true);
    setError(undefined);
    try {
      const { data } = await client.mutate<{ crmUndoBatchUpdate: BatchRecord }>({
        mutation: CRM_UNDO_BATCH_UPDATE,
        variables: { id: result.id },
      });
      setConfirmUndo(false);
      setBusy(false);
      await follow(data!.crmUndoBatchUpdate);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    onClose(changedSomething);
  };

  const f = form.fields;
  const admins = (choices.data?.crmAdmins ?? []).map((a) => ({ value: String(a.id), label: adminName(a) }));
  const stages = (choices.data?.stages ?? []).filter((s) => s.value).map((s) => s.value as string);
  const plans = choices.data?.crmMembershipPlans ?? [];

  const panel = (() => {
    switch (form.tab) {
      case 'people':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Choice
              label="Point person"
              value={f.clearPointPerson ? '' : f.pointPerson}
              disabled={f.clearPointPerson}
              onChange={(pointPerson) => setFields({ pointPerson })}
              options={admins}
              hint={f.clearPointPerson ? 'Cleared on the Clear field tab.' : 'The admin responsible for these people.'}
            />
            <Choice
              label="Support level"
              value={f.clearSupportLevel ? '' : f.supportLevel}
              disabled={f.clearSupportLevel}
              onChange={(supportLevel) => setFields({ supportLevel })}
              options={SUPPORT_LEVELS}
              hint={f.clearSupportLevel ? 'Cleared on the Clear field tab.' : undefined}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: { xs: 0.5, sm: 2 }, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Stage"
                value={f.clearStage ? '' : f.stage}
                disabled={f.clearStage}
                placeholder={NO_CHANGE}
                onChange={(event) => setFields({ stage: event.target.value })}
                inputProps={{ maxLength: BATCH_LIMITS.stage + 10, list: 'crm-batch-stages' }}
                InputLabelProps={{ shrink: true }}
              />
              <datalist id="crm-batch-stages">
                {stages.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              <Typography variant="caption" color="text.secondary">
                {f.clearStage ? 'Cleared on the Clear field tab.' : stages.length ? `In use: ${stages.join(', ')}.` : 'Type a stage.'}
              </Typography>
            </Box>
            <Choice label="Do not call" value={f.doNotCall} onChange={(v) => setFields({ doNotCall: v as YesNo })} options={YES_NO} />
            <Choice
              label="Do not contact"
              value={f.doNotContact}
              onChange={(v) => setFields({ doNotContact: v as YesNo })}
              options={YES_NO}
              hint="Closes email, calls and texts alike."
            />
            <Choice
              label="Is mobile number bad?"
              value={f.mobileNumberBad}
              onChange={(v) => setFields({ mobileNumberBad: v as YesNo })}
              options={YES_NO}
              hint="Where the mobile is the number they are called on, calls and texts stop until it is marked good. To move calls to another number, use the person's page."
            />
          </Box>
        );
      case 'consent':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Record consent given outside the app: a paper form, a call, an event. Opting people in needs a note saying how they
              gave it, kept with the batch.
            </Typography>
            <Choice label="Receive emails" value={f.emailOptIn} onChange={(v) => setFields({ emailOptIn: v as YesNo })} options={YES_NO} hint="No marks them unsubscribed today." />
            <Choice label="Receive text messages" value={f.smsOptIn} onChange={(v) => setFields({ smsOptIn: v as YesNo })} options={YES_NO} />
            <TextField
              size="small"
              type="date"
              label="Date consent was given"
              value={form.consentGivenAt}
              onChange={(event) => edit({ consentGivenAt: event.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={!opensConsent(f)}
              helperText={opensConsent(f) ? 'Optional. Kept with the batch.' : 'For a change that opts people in.'}
              sx={{ maxWidth: 260 }}
            />
          </Box>
        );
      case 'clear':
        return (
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Empties a field for everyone selected. Undo puts each value back.
            </Typography>
            {(
              [
                ['clearPointPerson', 'Point person'],
                ['clearSupportLevel', 'Support level'],
                ['clearStage', 'Stage'],
              ] as const
            ).map(([key, label]) => (
              <FormControlLabel key={key} control={<Checkbox checked={f[key]} onChange={(e) => setFields({ [key]: e.target.checked })} />} label={label} />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              NationBuilder can also clear home and mobile numbers. Here a number is marked bad instead (People tab), which keeps it on
              record.
            </Typography>
          </Box>
        );
      case 'tags':
        return (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.tags.action}
              onChange={(_, action) => action && edit({ tags: { ...form.tags, action } })}
              aria-label="Add or remove"
            >
              <ToggleButton value="ADD">Add tags</ToggleButton>
              <ToggleButton value="REMOVE">Remove tags</ToggleButton>
            </ToggleButtonGroup>
            <TargetPicker
              noun="tag"
              choices={tagChoices}
              selected={form.tags.targets}
              onChange={(targets) => edit({ tags: { ...form.tags, targets } })}
              max={10}
              allowCreate={false}
              loading={tagsQuery.loading}
              footer={
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                  The numbers are everyone in the CRM with each tag. The review says how many of these have it. To make a new tag, use
                  Add to tag on the contact list.
                </Typography>
              }
            />
          </Box>
        );
      case 'lists':
        return (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.list.action}
              onChange={(_, action) => action && edit({ list: { ...form.list, action } })}
              aria-label="Add or take off"
            >
              <ToggleButton value="ADD">Add to a list</ToggleButton>
              <ToggleButton value="REMOVE">Take off a list</ToggleButton>
            </ToggleButtonGroup>
            <TargetPicker
              noun="list"
              choices={listChoices}
              selected={form.list.targets}
              onChange={(targets) => edit({ list: { ...form.list, targets } })}
              max={1}
              allowCreate={false}
              onQueryChange={(listsQuery.data?.crmSegments.total ?? 0) > 200 ? setListText : undefined}
              loading={listsQuery.loading}
            />
          </Box>
        );
      case 'log':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Logs one identical contact on everyone selected, as logging a call does: it counts as their last contact. An outbound
              contact leaves out anyone marked do not call or do not contact.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField select size="small" label="Method" value={form.log.method} onChange={(e) => edit({ log: { ...form.log, method: e.target.value } })}>
                {METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Direction"
                value={form.log.direction}
                onChange={(e) => edit({ log: { ...form.log, direction: e.target.value as 'OUTBOUND' | 'INBOUND' } })}
              >
                <MenuItem value="OUTBOUND">We contacted them</MenuItem>
                <MenuItem value="INBOUND">They contacted us</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Contact status"
                value={form.log.outcome}
                onChange={(e) => edit({ log: { ...form.log, outcome: e.target.value } })}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>
                  <em>Say what happened</em>
                </MenuItem>
                {OUTCOMES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="datetime-local"
                label="When"
                value={form.log.occurredAt}
                onChange={(e) => edit({ log: { ...form.log, occurredAt: e.target.value } })}
                InputLabelProps={{ shrink: true }}
                helperText="Now, unless set."
              />
            </Box>
            <TextField
              size="small"
              label="Notes"
              multiline
              minRows={2}
              value={form.log.notes}
              onChange={(e) => edit({ log: { ...form.log, notes: e.target.value } })}
              helperText="On every one of the calls."
            />
            <FormControlLabel
              control={<Checkbox checked={form.log.closeFollowUps} onChange={(e) => edit({ log: { ...form.log, closeFollowUps: e.target.checked } })} />}
              label="Count this as the follow-up for anyone who has one open"
            />
          </Box>
        );
      case 'memberships':
        return plans.length === 0 && !choices.loading ? (
          <Alert severity="info" variant="outlined">
            There are no membership plans yet. NationBuilder's membership types come across with the migration; none are on this
            server.
          </Alert>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.membership.action}
              onChange={(_, action) => action && edit({ membership: { ...form.membership, action } })}
              aria-label="Membership change"
            >
              <ToggleButton value="ADD">Add</ToggleButton>
              <ToggleButton value="UPDATE">Update</ToggleButton>
              <ToggleButton value="REMOVE">Remove</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {form.membership.action === 'ADD'
                ? 'Gives a membership of the plan to everyone selected who has none of it. Existing memberships are left as they are.'
                : form.membership.action === 'UPDATE'
                  ? 'Changes the memberships of the plan these people already have. Nobody gains one.'
                  : 'Deletes their memberships of the plan. Each is recorded, so undo can put it back with its donations.'}
            </Typography>
            <TextField
              select
              size="small"
              label="Plan"
              value={form.membership.planId}
              onChange={(e) => edit({ membership: { ...form.membership, planId: e.target.value } })}
              sx={{ maxWidth: 360 }}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name} · {n(p.active)} active of {n(p.total)}
                </MenuItem>
              ))}
            </TextField>
            {form.membership.action !== 'REMOVE' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={form.membership.status}
                  onChange={(e) => edit({ membership: { ...form.membership, status: e.target.value } })}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">
                    <em>{form.membership.action === 'ADD' ? 'Active' : NO_CHANGE}</em>
                  </MenuItem>
                  {MEMBERSHIP_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="date"
                  label="Started"
                  value={form.membership.startedAt}
                  onChange={(e) => edit({ membership: { ...form.membership, startedAt: e.target.value } })}
                  InputLabelProps={{ shrink: true }}
                  helperText={form.membership.action === 'ADD' ? 'Today, unless set.' : NO_CHANGE}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Expires"
                  value={form.membership.clearExpiry ? '' : form.membership.expiresOn}
                  disabled={form.membership.clearExpiry}
                  onChange={(e) => edit({ membership: { ...form.membership, expiresOn: e.target.value } })}
                  InputLabelProps={{ shrink: true }}
                  helperText={form.membership.action === 'ADD' ? 'Never, unless set.' : NO_CHANGE}
                />
              </Box>
            )}
            {form.membership.action === 'UPDATE' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.membership.clearExpiry}
                    onChange={(e) => edit({ membership: { ...form.membership, clearExpiry: e.target.checked } })}
                  />
                }
                label="Clear the expiry date"
              />
            )}
          </Box>
        );
    }
  })();

  const count = preview?.matched;
  const changeLabel = BATCH_TABS.find((t) => t.tab === form.tab)?.label ?? '';

  return (
    <Dialog open onClose={close} fullScreen={phone} fullWidth maxWidth="md" aria-labelledby="crm-batch-title">
      <DialogTitle id="crm-batch-title" sx={{ pb: 0.5 }}>
        Batch update
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {step !== 'edit' && count !== undefined ? `${who}: ${people(count)}.` : `${who}.`}
        </Typography>
      </DialogTitle>

      {step === 'edit' && (
        <Tabs
          value={form.tab}
          onChange={(_, tab: BatchTab) => edit({ tab })}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="What to change"
          sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {BATCH_TABS.map(({ tab, label }) => (
            <Tab
              key={tab}
              value={tab}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {label}
                  {tabHasEdits(form, tab) && (
                    <Box component="span" aria-label="has changes" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  )}
                </Box>
              }
              sx={{ textTransform: 'none', minWidth: 0 }}
            />
          ))}
          {MISSING_TABS.map(({ label, why }) => (
            <MissingTab key={label} label={label} why={why} value={`missing:${label}`} />
          ))}
        </Tabs>
      )}

      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        {everyone && step !== 'done' && (
          <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
            No search or filter is set: this is everyone in the CRM.
          </Alert>
        )}

        {step === 'edit' && (
          <>
            <Box sx={{ pointerEvents: busy ? 'none' : undefined, opacity: busy ? 0.6 : 1 }}>{panel}</Box>
            <TextField
              size="small"
              label={TAB_GROUP[form.tab] === 'fields' && opensConsent(f) ? 'How did they give consent? (required)' : 'Note (optional)'}
              value={form.note}
              onChange={(e) => edit({ note: e.target.value })}
              multiline
              minRows={1}
              inputProps={{ maxLength: BATCH_LIMITS.note }}
              helperText="Kept with the batch in the history."
            />
            {elsewhere.length > 0 && (
              <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
                A batch makes one kind of change. This one is {changeLabel}; what you set on {elsewhere.join(' and ')} is kept here but not
                applied. Run it as its own batch afterwards.
              </Alert>
            )}
          </>
        )}

        {step === 'review' && preview && (
          <>
            <Box>
              <Overline>What will change</Overline>
              <PartsTable parts={preview.parts} done={false} />
            </Box>
            {preview.warnings.length > 0 && (
              <Alert severity="warning" variant="outlined">
                {preview.warnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </Alert>
            )}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {describePreview(preview)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The batch is recorded with your name and can be undone from the batch history: undo puts back each value it replaced,
              where nobody has changed it since.
            </Typography>
          </>
        )}

        {step === 'done' && result && (
          <>
            {isWorking(result) ? (
              <Box role="status" sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {result.status === 'UNDOING' ? 'Undoing' : 'Working'}: {progressOf(result).text}
                </Typography>
                <LinearProgress variant="determinate" value={progressOf(result).percent} aria-label="Progress" />
                <Typography variant="caption" color="text.secondary">
                  A batch this size goes on in the background. You can close this: it carries on, and the batch history shows how far
                  it has got.
                </Typography>
              </Box>
            ) : result.status === 'FAILED' ? (
              <Alert severity="error" role="status">
                {result.error ?? 'It stopped part way.'} What was done stays done: resume it, or undo it, from the batch history.
              </Alert>
            ) : (
              <Alert severity={result.undoneAt ? 'info' : 'success'} role="status">
                {result.undoneAt ? `Undone. ${describeUndo(result)}` : `${result.summary}: ${people(result.changed)} changed.`}
              </Alert>
            )}
            <Box>
              <Overline>What changed</Overline>
              <PartsTable parts={result.parts} done />
            </Box>
            {!result.undoneAt && result.canUndo && !isWorking(result) && (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {confirmUndo ? (
                  <Alert
                    severity="warning"
                    action={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button color="inherit" size="small" onClick={() => setConfirmUndo(false)} disabled={busy}>
                          Keep it
                        </Button>
                        <Button color="error" size="small" variant="contained" onClick={undo} disabled={busy}>
                          Undo
                        </Button>
                      </Box>
                    }
                  >
                    {result.undoNote}
                  </Alert>
                ) : (
                  <Box>
                    <Button variant="outlined" color="warning" onClick={() => setConfirmUndo(true)}>
                      Undo this batch
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {error !== undefined && (
          <Alert severity={selectionChangedTo(error) !== undefined ? 'warning' : 'error'}>
            {selectionChangedTo(error) !== undefined
              ? `The selection changed: it now holds ${people(selectionChangedTo(error) as number)}. Nothing was changed. Check the new numbers and confirm again.`
              : describeBatchError(error)}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        {step === 'edit' && (
          <>
            {problems.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', flexBasis: { xs: '100%', sm: 'auto' } }}>
                {problems[0]}
              </Typography>
            )}
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => review()}
              disabled={busy || problems.length > 0}
              startIcon={busy ? <CircularProgress size={16} color="inherit" aria-label="Counting" /> : undefined}
            >
              Review
            </Button>
          </>
        )}
        {step === 'review' && preview && (
          <>
            <Button
              onClick={() => {
                setStep('edit');
                setError(undefined);
              }}
              disabled={busy}
              sx={{ mr: 'auto' }}
            >
              Back
            </Button>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={isRisky(form) ? 'error' : 'primary'}
              onClick={run}
              disabled={busy || preview.matched === 0}
              startIcon={busy ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
            >
              {confirmBatchLabel(form, preview.matched)}
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button variant="contained" onClick={close} disabled={busy} autoFocus>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
